import auth from '@/services/auth'
import consts from '@/consts'
import logger from '@/services/logger'
import { Mutex } from 'async-mutex'

const callRoom = {
  updateParticipant: async function (participantId, enabled, muted) {
    try {
      var d = {}
      if (enabled === false || enabled === true) d.enabled = enabled
      if (muted === false || muted === true) d.muted = muted

      const resp = await auth._sendEvent({
        method: 'patch',
        url: '/v1/call-room/participants/' + participantId,
        data: d
      })
      return resp.data
    } catch (err) {
      return Promise.reject(err)
    }
  },
  createSession: async function (participantId, clientId) {
    try {
      // Send
      const resp = await auth._sendEvent({
        method: 'post',
        url: '/v1/call-room/sessions',
        data: {
          client_id: clientId,
          participant_id: participantId
        }
      })
      return resp.data
    } catch (err) {
      return Promise.reject(err)
    }
  },
  updateSession: async function (sessionId, clientId) {
    try {
      // Send
      const resp = await auth._sendEvent({
        method: 'patch',
        url: '/v1/call-room/sessions/' + sessionId,
        data: {
          client_id: clientId
        }
      })
      return resp.data
    } catch (err) {
      return Promise.reject(err)
    }
  },
  removeSession: async function (sessionId) {
    try {
      // Send
      const resp = await auth._sendEvent({
        method: 'delete',
        url: '/v1/call-room/sessions/' + sessionId
      })
      return resp.data
    } catch (err) {
      return Promise.reject(err)
    }
  }
}

class CallRoomSession {
  constructor (participantId, onError, wsEvent, modal, onJoin, onLeave) {
    this.as = {}
    this.cs = {}
    this.cds = {}
    this.cnts = {}
    this.j = false
    this.m = new Mutex()
    this.modal = modal
    this.onError = onError
    this.onJoin = onJoin
    this.onLeave = onLeave
    this.participantId = participantId
    this.wsEvent = wsEvent
  }
  onStart (data) {
    if (data && data.sessions) {
      data.sessions.forEach(session => this.start(session))
    } else {
      this.start()
    }
  }
  onStop (data) {
    if (data && data.sessions) {
      data.sessions.forEach(session => this.stop(session))
    } else {
      this.stop()
    }
  }
  setClientId (clientId) {
    // Update client id
    this.clientId = clientId

    // Create or update session
    if (!this.sessionId) {
      // Create session
      callRoom.createSession(this.participantId, this.clientId)
        .then(data => {
          // Store session id
          this.sessionId = data.session_id

          // Start
          if (data.start) {
            if (data.start.sessions) {
              // Loop through sessions
              data.start.sessions.forEach(session => { this.start(session) })
            } else {
              // Start
              this.start()
            }
          }
        })
        .catch(err => this.onError(err))
    } else {
      // Update session
      callRoom.updateSession(this.sessionId, this.clientId)
        .catch(err => this.onError(err))
    }
  }
  async stream () {
    const release = await this.m.acquire()
    const s = new Date()
    try {
      // Stream already exists
      if (this.s) return this.s

      // Show modal
      this.modal.show()

      // Get user media
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })

        // Store stream
        this.s = stream
        return stream
      } catch (err) {
        // Switch on error name
        switch (err.name) {
          case 'NotAllowedError':
            // Update msg
            err.msg = 'errors.not_allowed'
            break
        }
        return Promise.reject(err)
      }
    } finally {
      release()

      // Hide modal
      const e = new Date()
      if (e.getTime() - s.getTime() < 2000) {
        setTimeout(this.modal.hide, 2000 - (e.getTime() - s.getTime()))
      } else {
        this.modal.hide()
      }
    }
  }
  closeStream () {
    if (!this.s) return
    this.s.getTracks().forEach(track => track.stop())
    this.s = null
  }
  start (session, offer) {
    // Get stream
    this.stream()
      .then(stream => {
        // Join
        if (!this.j) {
          // Update
          this.j = true

          // Custom
          if (this.onJoin) this.onJoin()
        }

        // Do nothing
        if (!session) return

        // Start session
        this.startSession(session, offer, stream)
      })
      .catch(err => { this.onError(err) })
  }
  startSession (session, offer, stream) {
    // Create peer connection
    var pc = new RTCPeerConnection({
      iceServers: [
        {
          urls: 'stun:stun.l.google.com:19302'
        }
      ]
    })

    // Store peer connection
    this.cs[session.id] = pc

    // Handle ice candidate
    pc.onicecandidate = function (event) {
      // Send candidate
      this.wsEvent.send(consts.eventNames.callRoomCandidate, {
        candidate: event.candidate,
        from: { id: this.sessionId },
        to: { id: session.id }
      })
    }.bind(this)

    // Handle track
    pc.ontrack = function (event) {
      // Only process audio
      if (event.track.kind !== 'audio') return

      // Create audio
      this.as[session.id] = new Audio()
      this.as[session.id].srcObject = event.streams[0]

      // Mute
      this.as[session.id].muted = session.muted

      // Catch play error
      this.as[session.id].play().catch(err => console.error(err))
    }.bind(this)

    // Increment counter
    if (!this.cnts[session.id]) this.cnts[session.id] = 1
    const cnt = this.cnts[session.id]++

    // Handle stage changes
    pc.onconnectionstatechange = function () { logger.debug('webrtc-c2c-' + session.id + '#' + cnt, 'connection state changed to ' + pc.connectionState) }
    pc.oniceconnectionstatechange = function () {
      // Switch on state
      switch (pc.iceConnectionState) {
        case 'connected':
          // Send connected
          this.wsEvent.send(consts.eventNames.callRoomConnected, {
            from: { id: this.sessionId },
            to: { id: session.id }
          })
          break
        case 'failed':
        case 'disconnected':
        case 'closed':
          // Close webrtc
          this.closeWebRTC(session.id)
          break
      }

      // Log
      logger.debug('webrtc-c2c-' + session.id + '#' + cnt, 'ice connection state changed to ' + pc.iceConnectionState)
    }.bind(this)
    pc.onicegatheringstatechange = function () { logger.debug('webrtc-c2c-' + session.id + '#' + cnt, 'ice gathering state changed to ' + pc.iceGatheringState) }
    pc.onsignalingstatechange = function () { logger.debug('webrtc-c2c-' + session.id + '#' + cnt, 'signaling state changed to ' + pc.signalingState) }

    // Add tracks
    stream.getTracks().forEach(track => pc.addTrack(track, stream))

    // Do we have an offer?
    if (offer) {
      // Set remote description
      pc.setRemoteDescription(offer)
        .then(() => {
          // Process buffered candidates
          this.processBufferedCandidates(session.id)

          // Create answer
          pc.createAnswer()
            .then(answer => {
              // Set local description
              pc.setLocalDescription(answer)
                .then(() => {
                  // Send answer
                  this.wsEvent.send(consts.eventNames.callRoomAnswer, {
                    from: { id: this.sessionId },
                    session_description: answer,
                    to: { id: session.id }
                  })
                })
                .catch(err => this.onWebRTCError(err, session.id))
            })
            .catch(err => this.onWebRTCError(err, session.id))
        })
        .catch(err => this.onWebRTCError(err, session.id))
    } else {
      // Create offer
      pc.createOffer({ offerToReceiveAudio: 1 })
        .then(offer => {
          // Set local description
          pc.setLocalDescription(offer)
            .then(() => {
              // Send offer
              this.wsEvent.send(consts.eventNames.callRoomOffer, {
                from: { id: this.sessionId },
                session_description: offer,
                to: { id: session.id }
              })
            })
            .catch(err => this.onWebRTCError(err, session.id))
        })
        .catch(err => this.onWebRTCError(err, session.id))
    }
  }
  onAnswer (data) {
    // No from
    if (!data.from) return

    // No connection
    if (!this.cs[data.from.id]) return

    // Set remote description
    this.cs[data.from.id].setRemoteDescription(data.session_description)
      .then(() => this.processBufferedCandidates(data.from.id))
      .catch(err => this.onWebRTCError(err, data.from.id))
  }
  processBufferedCandidates (sessionId) {
    if (this.cds[sessionId]) {
      this.cds[sessionId].forEach(candidate => {
        this.cs[sessionId].addIceCandidate(candidate)
      })
      this.cds[sessionId] = []
    }
  }
  onCandidate (data) {
    // No from or no candidate
    if (!data.from || !data.candidate) return

    // No connection
    if (!this.cs[data.from.id] || !this.cs[data.from.id].remoteDescription) {
      // Buffer candidate
      if (!this.cds[data.from.id]) this.cds[data.from.id] = []
      this.cds[data.from.id].push(data.candidate)
      return
    }

    // Add candidate
    this.cs[data.from.id].addIceCandidate(data.candidate)
  }
  onOffer (data) {
    // No from
    if (!data.from) return

    // Start
    this.start(data.from, data.session_description)
  }
  onWebRTCError (err, sessionId) {
    // Close webrtc
    this.closeWebRTC(sessionId)

    // Process error
    this.onError(err)
  }
  stop (session) {
    if (!session) {
      this.closeAllWebRTC()
    } else {
      this.closeWebRTC(session.id)
    }
  }
  closeAllWebRTC () {
    // Loop through sessions
    for (var sessionId in this.cs) {
      this.closeWebRTC(sessionId)
    }

    // Leave
    if (Object.keys(this.cs).length === 0) this.leave()
  }
  leave () {
    // Close stream
    this.closeStream()

    // Update
    this.j = false

    // Custom
    if (this.onLeave) this.onLeave()
  }
  closeWebRTC (sessionId) {
    // No connection
    if (!this.cs[sessionId]) return

    // Close connection
    this.cs[sessionId].close()

    // Remove connection
    delete this.cs[sessionId]

    // Send disconnected
    this.wsEvent.send(consts.eventNames.callRoomDisconnected, {
      from: { id: this.sessionId },
      to: { id: sessionId }
    })

    // Leave
    if (Object.keys(this.cs).length === 0) this.leave()
  }
  async close () {
    // Close all webrt
    // Do it first as what's below may take some time and may be skipped in onWindowUnload
    this.closeAllWebRTC()

    // Remove session
    if (this.sessionId) {
      try {
        await callRoom.removeSession(this.sessionId)
      } catch (err) {
        this.onError(err)
      }
    }
  }
  onSessionsUpdated (data) {
    if (data.sessions) {
      data.sessions.forEach(session => {
        // Update mute
        if (typeof session.muted !== 'undefined') this.toggleMute(session)
      })
    }
  }
  toggleMute (session) {
    if (!this.as[session.id]) return
    this.as[session.id].muted = session.muted
  }
}

export {
  callRoom,
  CallRoomSession
}
